import React, { useState } from 'react';
import { X, Database, AlertCircle, Cloud, Key } from 'lucide-react';
import migrationService from '../../services/migrationService';

const StartMigrationModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    source_type: 'clio',
    s3_bucket: '',
    s3_prefix: '',
    primary_user_email: '',
    aws_access_key: '',
    aws_secret_key: '',
    aws_region: 'us-east-1',
    include_documents: true,
    dry_run: false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = {
        source_type: formData.source_type,
        source_config: {
          s3_bucket: formData.s3_bucket,
          s3_prefix: formData.s3_prefix,
          primary_user_email: formData.primary_user_email,
          aws_region: formData.aws_region
        },
        include_documents: formData.include_documents,
        dry_run: formData.dry_run
      };

      // Only include AWS credentials if provided
      if (formData.aws_access_key && formData.aws_secret_key) {
        payload.source_config.aws_access_key = formData.aws_access_key;
        payload.source_config.aws_secret_key = formData.aws_secret_key;
      }

      const result = await migrationService.createMigration(payload);
      onSuccess(result);
      onClose();
      // Reset form
      setFormData({
        source_type: 'clio',
        s3_bucket: '',
        s3_prefix: '',
        primary_user_email: '',
        aws_access_key: '',
        aws_secret_key: '',
        aws_region: 'us-east-1',
        include_documents: true,
        dry_run: false
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start migration');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-2">
            <Database className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Start Data Migration</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-start">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Source Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Import From
            </label>
            <select
              name="source_type"
              value={formData.source_type}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="clio">Clio</option>
              <option value="mycase">MyCase</option>
            </select>
          </div>

          {/* S3 Configuration */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Cloud className="h-5 w-5 text-gray-500" />
              <h3 className="font-medium text-gray-900">S3 Data Location</h3>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                S3 Bucket *
              </label>
              <input
                type="text"
                name="s3_bucket"
                value={formData.s3_bucket}
                onChange={handleInputChange}
                placeholder="data-escrow-clio-us-..."
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                S3 Prefix (optional)
              </label>
              <input
                type="text"
                name="s3_prefix"
                value={formData.s3_prefix}
                onChange={handleInputChange}
                placeholder="exports/"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Folder path within the bucket
              </p>
            </div>
          </div>

          {/* Primary User */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Primary User Email *
            </label>
            <input
              type="email"
              name="primary_user_email"
              value={formData.primary_user_email}
              onChange={handleInputChange}
              placeholder="admin@firm.com"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              This user will be assigned as owner of unmapped records
            </p>
          </div>

          {/* AWS Credentials */}
          <div className="space-y-4 border-t pt-4">
            <div className="flex items-center space-x-2">
              <Key className="h-5 w-5 text-gray-500" />
              <h3 className="font-medium text-gray-900">AWS Credentials (optional)</h3>
            </div>
            <p className="text-sm text-gray-500">
              Leave empty to use server's default credentials
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Access Key
                </label>
                <input
                  type="text"
                  name="aws_access_key"
                  value={formData.aws_access_key}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Secret Key
                </label>
                <input
                  type="password"
                  name="aws_secret_key"
                  value={formData.aws_secret_key}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                AWS Region
              </label>
              <select
                name="aws_region"
                value={formData.aws_region}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="us-east-1">US East (N. Virginia)</option>
                <option value="us-west-2">US West (Oregon)</option>
                <option value="eu-west-1">EU (Ireland)</option>
              </select>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                name="include_documents"
                checked={formData.include_documents}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                Include documents (may be slow for large firms)
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="dry_run"
                checked={formData.dry_run}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                Dry run (preview without committing changes)
              </label>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Starting...' : 'Start Migration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StartMigrationModal;
